/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from 'react-apollo';
import { defineMessages, FormattedMessage } from 'react-intl-next';
import { styled } from '@compiled/react';
// We have deprecated unstated. Please use react-sweet-state instead

import { Subscribe } from 'unstated';

import { token } from '@atlaskit/tokens';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	ARCHIVED_PAGE_BANNER_EXPERIENCE,
	RESTORE_PAGES_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName';

import {
	ExperienceFailure,
	ExperienceStart,
	ExperienceSuccess,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker/entry-points/component';

import type { ExperienceAttributes } from '@confluence/experience-tracker/entry-points/ExperienceEvent';
import { ScreenEvent } from '@confluence/analytics';
import { DialogsStateContainer } from '@confluence/dialogs';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary';
import { canRestoreContent } from '@confluence/content-types-utils/entry-points/contentOperationsUtils';
import { LoadableLazy } from '@confluence/loadable';

import { fireUIClickEvent } from './AnalyticsEvents';
import { ArchiveIcon } from './img/ArchiveIcon.js';
import type {
	ArchivedPageBannerV2ArchivedPageBannerQuery as ArchivedPageBannerV2ArchivedPageBannerQuery$data,
	ArchivedPageBannerV2ArchivedPageBannerQueryVariables as ArchivedPageBannerV2ArchivedPageBannerQuery$variables,
} from './__types__/ArchivedPageBannerV2ArchivedPageBannerQuery';
import type {
	ArchivedPageBannerV2ArchivedPageBannerContainerQuery as ArchivedPageBannerV2ArchivedPageBannerContainerQuery$data,
	ArchivedPageBannerV2ArchivedPageBannerContainerQueryVariables as ArchivedPageBannerV2ArchivedPageBannerContainerQuery$variables,
} from './__types__/ArchivedPageBannerV2ArchivedPageBannerContainerQuery';
import { CONTENT_STATUS_ARCHIVED } from './archivingConstants';
import { ArchivedPageBannerNote, ArchivePageActionLink } from './ArchivedPageBannerNote';

export const LazyUnarchivePagesLoader = LoadableLazy({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-UnarchivePagesLoader" */ './UnarchivePagesLoader'))
			.UnarchivePagesLoader,
});

export const ARCHIVED_PAGE_BANNER_ANALYTICS_SOURCE = 'archivedPageBanner';

export const ARCHIVED_PAGE_BANNER_RESTORE_LINK_TESTID = 'archive-page-banner-restore-link';

const ARCHIVE_PAGES_DOCS_LINK = 'https://support.atlassian.com/confluence-cloud/docs/archive-pages';

const BannerWrapperCommonStyles = xcss({
	display: 'flex',
	alignItems: 'flex-start',
	backgroundColor: 'color.background.neutral.bold',
	borderRadius: 'radius.large',
	color: 'color.text.inverse',
	minWidth: '450px',
	width: '100%',
	paddingBlock: 'space.200',
	paddingInline: 'space.300',
	boxSizing: 'border-box',
	marginBottom: 'space.0',
});
const marginBottomStyles = xcss({ marginBottom: 'space.200' });
const marginTopStyles = xcss({ marginTop: 'space.200' });
const marginBothStyles = xcss({ marginTop: 'space.200', marginBottom: 'space.200' });

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BannerTextWrapper = styled.div({
	flexGrow: 1,
	marginRight: token('space.300'),
});

// The icon must have a wrapping <div> since AK icons don't accept a `padding` prop
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconWrapper = styled.div({
	marginRight: token('space.250'),
	display: 'flex',
	flexShrink: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BannerTitleText = styled.div({
	font: token('font.body'),
	fontWeight: token('font.weight.medium'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BannerActionText = styled.div({
	font: token('font.body'),
	flexShrink: 0,
});

export const i18n = defineMessages({
	restoreLinkMessage: {
		id: 'archive-pages.page-banner.link.unarchive',
		defaultMessage: 'Restore',
		description: 'Link that allows the user to unarchive the page being viewed',
	},
	learnMoreLinkMessage: {
		id: 'archive-pages.page-banner.link.learn-more',
		defaultMessage: 'Learn more',
		description:
			'Link directing user to https://support.atlassian.com/confluence-cloud/docs/archive-pages for more information about archiving, from an archived page',
	},
	hasRestoreContentPermissionBannerMessage: {
		id: 'archive-pages.content-banner.message-restorable',
		defaultMessage: 'This content is archived. Restore to make changes.',
		description:
			'Message to inform the user that the content being viewed is archived and that they can restore it to make changes',
	},
	noRestoreContentPermissionBannerMessage: {
		id: 'archive-pages.content-banner.message-unrestorable',
		defaultMessage: 'This content is archived.',
		description:
			'Message to inform the user that the content being viewed is archived, when they have no permission to restore',
	},
});

type ArchivedPageBannerProps = {
	contentId: string;
	marginStyle?: 'none' | 'bottom' | 'top' | 'both';
};

const ArchivedPageBannerComponent = ({
	contentId,
	marginStyle = 'bottom',
}: ArchivedPageBannerProps) => {
	// We need two queries here: one to see if the content is archived or not, and the second to get
	// the archive note info if the content is archived. We MUST NOT do the second query for the
	// archive note info when the content is NOT archived because that second query will fail!
	//
	// And to properly handle Experience Tracking when using "cache-and-network" fetch then both
	// queries have to be in the same component because if you break the two queries into separate
	// nested components (as was done in an early version of this code), then the inner component is
	// likely to get unmounted when the query goes back to `loading` state on "network" part of a
	// cached fetch. The inner component loses any state info on the remount and can't remember if it
	// has sent the Experience Tracking events.  So it is simpler to have one single component with
	// both queries,
	//
	// These 2 queries are identical to ArchivedPageBannerContainerQuery and ArchivedPageBannerQuery
	// in ./preloadArchivedPageBannerV2Queries.ts
	// They are inlined here because of Relay
	const {
		error: statusError,
		data: statusData,
		refetch: statusRefetch,
	} = useQuery<
		ArchivedPageBannerV2ArchivedPageBannerContainerQuery$data,
		ArchivedPageBannerV2ArchivedPageBannerContainerQuery$variables
	>(
		gql`
			query ArchivedPageBannerV2ArchivedPageBannerContainerQuery($contentId: ID) {
				content(id: $contentId, status: ["archived", "current"]) {
					nodes {
						id
						status
					}
				}
			}
		`,
		{
			variables: { contentId },
			fetchPolicy: 'cache-and-network',
		},
	);

	const contentStatus = statusData?.content?.nodes?.[0]?.status;
	const isContentArchived = contentStatus === CONTENT_STATUS_ARCHIVED;
	// Note that when the user changes to a different page/content item, this component remains
	// mounted and will fetch the data for the new page, but the old "statusData" is still
	// available while the new data is being fetched. So the new data isn't really ready until
	// we see that the data has the correct contentId in it.
	const isStatusDataAvailable = contentStatus && statusData?.content?.nodes?.[0]?.id === contentId;

	// If the content is not archived, then we MUST NOT do the second query for the archive note
	// info because that query will fail!
	const { error: noteDataError, data: noteData } = useQuery<
		ArchivedPageBannerV2ArchivedPageBannerQuery$data,
		ArchivedPageBannerV2ArchivedPageBannerQuery$variables
	>(
		gql`
			query ArchivedPageBannerV2ArchivedPageBannerQuery($contentId: ID) {
				content(id: $contentId, status: ["archived", "current"]) {
					nodes {
						id
						type
						operations {
							operation
							targetType
						}
						archivedContentMetadata {
							archiveNote
						}
						history {
							lastUpdated {
								by {
									... on KnownUser {
										accountId
									}
									... on UnknownUser {
										accountId
									}
								}
							}
						}
					}
				}
			}
		`,
		{
			variables: { contentId },
			skip: !isContentArchived,
			fetchPolicy: 'cache-and-network',
		},
	);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const [editingNote, setEditingNote] = useState(false);

	// We need to keep the success atrributes object reference constant, as creating and assigning a
	// whole new object on every render pass will cause ExperienceSuccess's useEffect() to think
	// something has changed and it will fire the "success" event again. But if we keep the same
	// object across renders and just set the "archivedPageBannerShown" value, then ExperienceSuccess
	// sees no change there and will only fire again if something else has changed.
	const successAttributes: ExperienceAttributes = useMemo(
		() => ({
			archivedPageBannerShown: false,
		}),
		[],
	);

	const getUnarchiveDialogOpener = useCallback(
		(dialogs: DialogsStateContainer) => () => {
			fireUIClickEvent({
				createAnalyticsEvent,
				actionSubject: 'restoreButton',
				actionSubjectId: contentId,
				source: ARCHIVED_PAGE_BANNER_ANALYTICS_SOURCE,
			});

			experienceTracker.start({
				name: RESTORE_PAGES_EXPERIENCE,
			});

			dialogs.showModal(LazyUnarchivePagesLoader, {
				contentId,
				onPageRestore: statusRefetch,
				source: ARCHIVED_PAGE_BANNER_ANALYTICS_SOURCE,
			});
		},
		[contentId, createAnalyticsEvent, experienceTracker, statusRefetch],
	);

	const fireLearnMoreEvent = useCallback(
		() =>
			fireUIClickEvent({
				createAnalyticsEvent,
				actionSubject: 'link',
				actionSubjectId: 'restoreLearnMore',
				source: ARCHIVED_PAGE_BANNER_ANALYTICS_SOURCE,
			}),
		[createAnalyticsEvent],
	);

	const noteDataContent = noteData?.content?.nodes?.[0];

	const userCanRestorePage = canRestoreContent(noteDataContent?.operations || []);

	const archiveNote = noteDataContent?.archivedContentMetadata?.archiveNote || '';
	// accountId does not exist on Anonymous user type, so we need to use bracket notation to get the property or undefined
	const archiverAccountId: string | null | undefined =
		noteDataContent?.history?.lastUpdated?.by?.['accountId'];

	// Similar to the status data, if the user changes to a different page/content item, this
	// component will remain mounted and will fetch the data for the new page, but the old "noteData"
	// is still available while the new data is being fetched. So the new data isn't really ready
	// until we see that the data has the correct contentId in it.
	const isNoteDataAvailable = noteDataContent && noteDataContent?.id === contentId;

	// Only update the value; creating and assigning a whole new object will cause ExperienceSuccess's
	// useEffect() to think something has changed and it will fire the "success" event again.
	successAttributes.archivedPageBannerShown = isContentArchived;

	const error = statusError || noteDataError;
	return (
		<>
			{error && (
				<ErrorDisplay error={error}>
					<ExperienceFailure name={ARCHIVED_PAGE_BANNER_EXPERIENCE} error={error} />
				</ErrorDisplay>
			)}
			{/* Experience Success is when we've sucessfully gotten all the query data we need.
  We must have the status info (whether the content item is archived or not), and we only
  need the note data if the item is archived.
  (Note we are only successful if the specific data fields are retreived; if we get some
  data back but the specific data fields we need aren't returned, then that's a failure. */}
			{isStatusDataAvailable && (!isContentArchived || !!isNoteDataAvailable) && (
				// Note that using a "key" prop with the "contentId" here will make React create a new
				// ExperienceSuccess instance, which is useful because we can remain mounted when the user
				// changes to a different content item
				<ExperienceSuccess
					name={ARCHIVED_PAGE_BANNER_EXPERIENCE}
					key={contentId}
					attributes={successAttributes}
				/>
			)}
			{/* Note that we need to look at the query data state instead of the "loading" state because
  the "cache-and-network" fetch policy will return the cached data and then go back to the
  "loading" state to pull the current data from the network - but the cached data is still
  returned until the network fetch is completed. But if we have "!noteDataLoading" here
  then the banner would flicker when we return to the "loading" state. */}
			{isStatusDataAvailable && isContentArchived && noteDataContent ? (
				<Subscribe to={[DialogsStateContainer]}>
					{(dialogs: DialogsStateContainer) => (
						<>
							<ScreenEvent
								name={ARCHIVED_PAGE_BANNER_ANALYTICS_SOURCE}
								id="" // "id" is a required prop, but is not actually sent in the event
								attributes={{ canRestore: userCanRestorePage }}
							/>
							<Box
								xcss={[
									BannerWrapperCommonStyles,
									marginStyle === 'bottom' && marginBottomStyles,
									marginStyle === 'top' && marginTopStyles,
									marginStyle === 'both' && marginBothStyles,
								]}
							>
								<IconWrapper>
									<ArchiveIcon fill={token('color.icon.inverse')} />
									{/* Icon doesn't need an aria-label because the element's label tells aria what the banner is */}
								</IconWrapper>
								<BannerTextWrapper>
									<BannerTitleText>
										<FormattedMessage
											{...(userCanRestorePage
												? i18n.hasRestoreContentPermissionBannerMessage
												: i18n.noRestoreContentPermissionBannerMessage)}
										/>
									</BannerTitleText>
									<ArchivedPageBannerNote
										note={archiveNote}
										archiverAccountId={archiverAccountId}
										contentId={contentId}
										editingNote={editingNote}
										setEditingNote={setEditingNote}
									/>
								</BannerTextWrapper>
								{!editingNote && (
									<BannerActionText>
										{userCanRestorePage ? (
											<ArchivePageActionLink
												data-testid={ARCHIVED_PAGE_BANNER_RESTORE_LINK_TESTID}
												onClick={getUnarchiveDialogOpener(dialogs)}
											>
												<FormattedMessage {...i18n.restoreLinkMessage} />
											</ArchivePageActionLink>
										) : (
											<ArchivePageActionLink
												target="_blank"
												href={ARCHIVE_PAGES_DOCS_LINK}
												onClick={fireLearnMoreEvent}
											>
												<FormattedMessage {...i18n.learnMoreLinkMessage} />
											</ArchivePageActionLink>
										)}
									</BannerActionText>
								)}
							</Box>
						</>
					)}
				</Subscribe>
			) : null}
		</>
	);
};

export const ArchivedPageBanner = ({ contentId, marginStyle }: ArchivedPageBannerProps) => (
	<ErrorBoundary attribution={Attribution.ADMIN_EXPERIENCE}>
		{/* Using a "key" prop with the "contentId" here will make React create a new ExperienceStart
        instance whenever the contentId changes. This is useful because we can remain mounted
        when the user changes to a different content item but we will want to start tracking the
        new page's banner as a new experience. */}
		<ExperienceStart name={ARCHIVED_PAGE_BANNER_EXPERIENCE} key={contentId} />
		<ArchivedPageBannerComponent contentId={contentId} marginStyle={marginStyle} />
	</ErrorBoundary>
);
